import { SiteBody } from "@vviinn/user-managment-client-ts";
import { FC, FormEvent, useState } from "react";
import { useApi } from "../../api";
import { Restricted } from "../../components/Restricted";

import {
  useAppDispatch,
  useAppSelector,
  upsertSite,
  changeActiveSite,
  getCompany,
} from "../../store";

import {
  configWithSegmentation,
  defaultInitEmbedderConfig,
  trackingConfig,
} from "../../site";
import { transformFormData } from "../../helpers";
import { SiteItem } from "@vviinn/user-managment-client-ts";
import { NamedRow } from "../../components/GeneralUI/NamedRow/NamedRow";
import { AiMethodsEditBlock } from "../../site/components/AI/AiMethodsEditBlock";
import { DetailedWrapper } from "../../ui/layout/general/DetailedWrapper/DetailedWrapper";
import { NamedSwitch } from "../../ui/layout/molecules/NamedSwitch";
import { INPUT_FIELD_STYLES } from "../../ui/layout/detailedPages/styles";
import { FormRow, SubSectionWithStyles } from "../../ui/layout";

export const SiteNew: FC<React.PropsWithChildren<{}>> = () => {
  const company = useAppSelector((state) => state.company.company);
  const dispatch = useAppDispatch();
  const { getApi } = useApi();

  const backLink = `/company/${company?.id}/sites`;

  const [embeddersConfig, setEmbeddersConfig] = useState(
    defaultInitEmbedderConfig,
  );
  const [useBarraquito, setUseBarraquito] = useState(false);
  const [groupItems, setGroupItems] = useState(false);

  const handleFormSubmit = async (e: FormEvent) => {
    if (!company) return;
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const siteBody = transformFormData<SiteBody>(data);
    siteBody.embedders = embeddersConfig;
    siteBody.useBarraquito = useBarraquito;
    siteBody.productUniquenessLevel = groupItems
      ? "item_group_id"
      : "product_code";
    const embedderConfig = configWithSegmentation(siteBody);
    const companyId = company.id;

    const { track, ...rest } = siteBody;
    const booleanTrack = track ?? false;

    const trackingConfiguration = trackingConfig(booleanTrack);

    const body = {
      companyId: companyId,
      siteBody: {
        ...rest,
        trackingConfiguration,
        embedders: embedderConfig,
      },
    };

    const api = await getApi();
    const response = await dispatch(upsertSite({ api, value: body }));
    if (response.meta.requestStatus === "fulfilled") {
      dispatch(getCompany({ api, value: companyId }));
      const siteCredentials = response.payload as SiteItem;
      dispatch(changeActiveSite(siteCredentials));
      return `/company/${companyId}/site/${siteCredentials.id}`;
    }
  };

  return (
    <DetailedWrapper
      createItemAction={handleFormSubmit}
      backLink={backLink}
      detailedPageType="new"
      detailedPage="site"
    >
      <SubSectionWithStyles title="General Information" isSubHeader={false}>
        <NamedRow title="Company">
          {company?.name ?? "Unknown company"}
        </NamedRow>

        <FormRow labelText="Domain" labelHtmlFor="domain">
          <input
            type="text"
            name="domain"
            id="domain"
            required
            className={INPUT_FIELD_STYLES}
          />
        </FormRow>

        <FormRow labelText="Description" labelHtmlFor="description">
          <input
            type="text"
            name="description"
            id="description"
            required
            className={INPUT_FIELD_STYLES}
          />
        </FormRow>

        <Restricted permissions="site:advanced_config:view">
          <NamedSwitch
            title="Tracking"
            value={true}
            customId="track"
            styleVariant="newPage"
          />
        </Restricted>
      </SubSectionWithStyles>

      <Restricted permissions="site:advanced_config:view">
        <SubSectionWithStyles title="Products Feed" isSubHeader={false}>
          <NamedSwitch
            title="Group Items"
            value={groupItems}
            permissions="site:advanced_config:update"
            onChange={(value) => setGroupItems(value)}
            styleVariant="newPage"
          />
        </SubSectionWithStyles>

        <SubSectionWithStyles
          title="AI Methods"
          isSubHeader={false}
          withGroup={false}
          className="gap-5"
        >
          <AiMethodsEditBlock
            embeddersConfig={embeddersConfig}
            setEmbeddersConfig={setEmbeddersConfig}
            useBarraquito={useBarraquito}
            setUseBarraquito={setUseBarraquito}
          />
        </SubSectionWithStyles>
      </Restricted>
    </DetailedWrapper>
  );
};
